import Link from 'next/link';

import { useDispatch, useSelector } from 'react-redux';
import styles from 'styles/modules/newsAlert/Detail.module.scss';
import Button from 'components/Button';
import { news_alerts } from 'lang/en.json';
import { postReq } from 'api';
import { addAlertResponse, alertSelector } from 'store/alert';
import { authSelector } from 'store/auth';
import { useEffect, useState, useRef } from 'react';
import { handleHidePopup, handleShowPopup, setPopupConfig } from 'store/ux';
import LimitedTextArea from 'components/Sidescreen/LimitedTextArea';
import { useRouter } from 'next/router';
import { formatResponseReset } from 'helpers/date';
import { br2nl } from '../../helpers/strings';
import getResponseUpgradeCopy from 'utils/response-upgrade-copy';
import {
    FREE_COVERAGE_REACHED,
    freeCoverageMessage,
    getFreeCoverage,
} from 'utils/free-coverage';

export const RESPONSE_MAX_CHARACTERS = 1000;
export const VIDEO_MAX_SIZE_MB = 100;
export const VIDEO_MAX_DURATION_SEC = 30;
export const ALLOWED_VIDEO_TYPES = [
    'video/mp4',
    'video/quicktime',
    'video/webm',
];

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const isFTEditorByRole = (author) => {
    if (!author?.roles || !Array.isArray(author.roles)) return false;
    return author.roles.some((role) => role.name === 'FT_EDITOR');
};

const AddResponse = ({ alertId, alertData }) => {
    const { error, loading, alert } = useSelector(alertSelector);
    const { data: companyState } = useSelector(({ company }) => company);
    const {
        responseCredit,
        isContributingEditor,
        company: ownSlug,
    } = useSelector(authSelector);
    const videoInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const dispatch = useDispatch();
    const [responseBody, setResponseBody] = useState('');
    const [company, setCompany] = useState({});
    const [videoFile, setVideoFile] = useState(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
    const [videoError, setVideoError] = useState('');

    const router = useRouter();
    const upgradeToPremiumLink = `/${company.slug}/subscription`;

    useEffect(() => {
        setCompany(companyState);
    }, [companyState]);

    // Determine if this is an FT Editor alert. An anonymised one hides FT from the author, so the
    // API says it with responseLimitExempt.
    const isFTEditorAlert =
        alertData?.responseLimitExempt ||
        alert?.responseLimitExempt ||
        alertData?.author?.isFTEditor ||
        alert?.author?.isFTEditor ||
        isFTEditorByRole(alertData?.author) ||
        isFTEditorByRole(alert?.author);

    // Calculate response limits once
    const totalResponses =
        (company.plan?.responsesLeft || 0) + (responseCredit || 0);

    // The alert's own profile answers as its story's source, outside the monthly allowance.
    const isOwnAlert =
        company.slug === ownSlug &&
        Boolean(company.id) &&
        (alertData?.profileId ?? alert?.profileId) === company.id;

    // Free has no monthly allowance any more: unlimited until the first coverage, then blocked.
    const hasUnlimitedResponses =
        company.plan?.type === 'free' ||
        company.plan?.type === 'platinum' ||
        isContributingEditor ||
        isFTEditorAlert ||
        isOwnAlert;

    const isResponseLimitReached =
        !hasUnlimitedResponses && totalResponses <= 0;

    const shouldShowUpgradeMessages = !hasUnlimitedResponses;

    const upgradeCopy = getResponseUpgradeCopy({
        planType: company.plan?.type,
        totalResponses,
        hasUnlimitedResponses,
    });

    // Only shown once the allowance is spent — while responses remain they may be
    // purchased credits, which do not reset with the plan period.
    const responseResetDate = formatResponseReset(
        company.plan?.responsePeriodEnd
    );

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!loading) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!loading) setIsDragging(true);
    };

    const validateVideoDuration = (file) => {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = () => {
                window.URL.revokeObjectURL(video.src);
                if (video.duration > VIDEO_MAX_DURATION_SEC) {
                    const duration = formatDuration(video.duration);
                    const maxDuration = formatDuration(VIDEO_MAX_DURATION_SEC);
                    setVideoError(
                        `Video duration (${duration}) exceeds the maximum allowed duration of ${maxDuration}. Please select a shorter video.`
                    );
                    resolve(false);
                }
                resolve(true);
            };

            video.onerror = () => {
                setVideoError(
                    'Error validating video duration. Please try another video.'
                );
                resolve(false);
            };

            video.src = URL.createObjectURL(file);
        });
    };

    const validateVideoSize = (file) => {
        const maxSize = VIDEO_MAX_SIZE_MB * 1024 * 1024;
        if (file.size > maxSize) {
            const fileSize = formatFileSize(file.size);
            const maxSizeFormatted = formatFileSize(maxSize);
            setVideoError(
                `Video size (${fileSize}) exceeds the maximum allowed size of ${maxSizeFormatted}. Please select a smaller video.`
            );
            return false;
        }
        return true;
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (loading) return;

        const file = e.dataTransfer.files[0];
        if (file) {
            if (!validateVideoSize(file)) return;
            if (!(await validateVideoDuration(file))) return;
            validateAndSetVideo(file);
        }
    };

    const validateAndSetVideo = async (file) => {
        setVideoError('');

        if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
            setVideoError('Please upload MP4, MOV, or WebM files only');
            return;
        }

        if (!validateVideoSize(file)) return;
        if (!(await validateVideoDuration(file))) return;

        setVideoFile(file);
        setVideoPreviewUrl(URL.createObjectURL(file));
    };

    const handleVideoChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!validateVideoSize(file)) return;
            if (!(await validateVideoDuration(file))) return;
            validateAndSetVideo(file);
        }
    };

    const removeVideo = () => {
        setVideoFile(null);
        setVideoPreviewUrl('');
        if (videoInputRef.current) {
            videoInputRef.current.value = '';
        }
    };

    const onSubmit = async () => {
        if (!hasUnlimitedResponses && totalResponses === 0) {
            dispatch(handleShowPopup());
            dispatch(
                setPopupConfig({
                    onConfirm: () => {
                        router.push(upgradeToPremiumLink);
                        dispatch(handleHidePopup());
                    },
                    onCancel: () => dispatch(handleHidePopup()),
                    title: responseResetDate
                        ? `Look at you, you media machine! You've maxed out your News Alert responses — your next one arrives on ${responseResetDate}. Or go Plus or Platinum now and keep going.`
                        : "Look at you, you media machine! Get you, you've maxed out your one News Alert response per 30 days. Now it's time to go Plus or Platinum. Let's do this.",
                    confirmTitle: 'Upgrade now',
                    cancelTitle: 'Cancel',
                })
            );
            return;
        }

        const formData = new FormData();
        formData.append('body', responseBody);

        if (videoFile) {
            formData.append('video', videoFile, videoFile.name);
        }

        dispatch(
            addAlertResponse({
                id: alertId,
                data: formData,
            })
        );
    };

    const freeCoverage = isFTEditorAlert
        ? null
        : getFreeCoverage(company.plan, error);
    // The company slice holds the last profile loaded (another expert's page, or the previous
    // member's after a logout) until this page's fetch of the member's own one resolves.
    const blockedAhead =
        !!freeCoverage &&
        error?.code !== FREE_COVERAGE_REACHED &&
        !!company.id &&
        company.slug === ownSlug;

    // A refused response is what sends the upgrade email, and this screen stops the member before
    // they submit one, so it asks for the email itself; the API sends at most one a week.
    useEffect(() => {
        if (blockedAhead) {
            postReq(`profiles/${company.id}/free-coverage/notice`).catch(
                () => {}
            );
        }
    }, [blockedAhead, company.id]);

    if (freeCoverage) {
        return (
            <>
                <p className={styles.responseInfo}>
                    {freeCoverageMessage(freeCoverage)}
                </p>
                <div className={[styles.cta, styles.upgradeCta].join(' ')}>
                    <Button
                        title="Upgrade"
                        onClick={() => router.push(upgradeToPremiumLink)}
                        classes={[styles.button]}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            {isResponseLimitReached &&
            !isFTEditorAlert &&
            shouldShowUpgradeMessages ? (
                <div className={styles.responseInfo}>
                    {upgradeCopy?.variant === 'free-wall' ? (
                        <>
                            You&apos;ve used your 1 FREE News Alert response
                            this month. If you&apos;d like to respond, please{' '}
                            <Link href={upgradeToPremiumLink}>
                                upgrade to Newspage Plus or Newspage Pro
                            </Link>
                            . The more alerts you respond to, the more visible
                            you are in AI search, the more chances you have of
                            media coverage, and the sooner your Newspage becomes
                            a Page 1 trust asset for your name and company. All
                            subs can be cancelled at any time.
                        </>
                    ) : (
                        <>
                            {responseResetDate
                                ? `You have no responses left until ${responseResetDate}.`
                                : 'You currently have 0 responses left.'}{' '}
                            <Link href={upgradeToPremiumLink}>
                                Upgrade to Platinum
                            </Link>{' '}
                            for unlimited responses with professional editing.
                        </>
                    )}
                </div>
            ) : (
                <>
                    <LimitedTextArea
                        classes={[styles.responseBox]}
                        placeholder="Write your masterpiece here. And remember, no waffle."
                        callback={(value) => setResponseBody(value)}
                        limit={RESPONSE_MAX_CHARACTERS}
                        disabled={loading}
                    />
                    <div className={styles.videoUpload}>
                        {!videoPreviewUrl && (
                            <div
                                className={`${styles.uploadArea} ${
                                    isDragging ? styles.dragging : ''
                                } ${loading ? styles.disabled : ''}`}
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() =>
                                    !loading && videoInputRef.current?.click()
                                }
                            >
                                <input
                                    type="file"
                                    accept="video/mp4,video/quicktime,video/webm"
                                    onChange={handleVideoChange}
                                    ref={videoInputRef}
                                    className={styles.videoInput}
                                    disabled={loading}
                                />
                                <div className={styles.uploadIcon}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M12 16L12 8"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M9 11L12 8L15 11"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M8 16H16"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </div>
                                <div className={styles.uploadText}>
                                    <p>
                                        Drag and drop your video here or{' '}
                                        <span>browse</span>
                                    </p>
                                    <small>
                                        MP4, MOV or WebM (max.{' '}
                                        {VIDEO_MAX_SIZE_MB}MB,{' '}
                                        {VIDEO_MAX_DURATION_SEC} seconds)
                                    </small>
                                </div>
                            </div>
                        )}
                        {videoError && (
                            <div className={styles.errorContainer}>
                                <p className={styles.error}>{videoError}</p>
                                <button
                                    className={styles.tryAgain}
                                    onClick={() => {
                                        setVideoError('');
                                        videoInputRef.current?.click();
                                    }}
                                >
                                    Try again
                                </button>
                            </div>
                        )}
                        {videoPreviewUrl && (
                            <div className={styles.videoPreview}>
                                <video
                                    src={videoPreviewUrl}
                                    controls
                                    className={styles.previewVideo}
                                />
                                <button
                                    onClick={removeVideo}
                                    className={styles.removeVideo}
                                    disabled={loading}
                                >
                                    Remove video
                                </button>
                            </div>
                        )}
                        {loading && videoFile && (
                            <div className={styles.uploadingState}>
                                <div className={styles.uploadStatus}>
                                    <div
                                        className={styles.uploadingSpinner}
                                    ></div>
                                    <p>Uploading video... </p>
                                </div>
                                <p className={styles.uploadWarning}>
                                    Please do not exit or refresh the page while
                                    uploading
                                </p>
                            </div>
                        )}
                    </div>
                </>
            )}
            {upgradeCopy?.lead && (
                <div className={styles.responseInfo}>
                    {upgradeCopy.lead}{' '}
                    <Link href={upgradeToPremiumLink}>{upgradeCopy.cta}</Link>.
                </div>
            )}
            {error?.message && <p className={styles.error}>{error.message}</p>}
            <div className={styles.cta}>
                <Button
                    title={
                        loading
                            ? 'Uploading...'
                            : news_alerts.responses.post_response
                    }
                    onClick={onSubmit}
                    classes={[styles.button]}
                    disabled={
                        br2nl(responseBody).length > RESPONSE_MAX_CHARACTERS ||
                        isResponseLimitReached ||
                        !!videoError ||
                        loading
                    }
                />
            </div>
        </>
    );
};

export default AddResponse;
